import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';

/** `GET /api/gus/settings` (owner only): never the key — only whether one is saved, when, and why it cannot be used. */
export type GusSettingsView = { keySet: boolean; keySavedAt: string | null; error: string | null };

const SETTINGS_KEY = ['gus', 'settings'] as const;
const WHEN = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const errorText = (e: unknown, fallback: string) => {
  if (!(e instanceof ApiError)) return fallback;
  const issues = fieldIssues(e);
  return issues.length > 0 ? issues.map((i) => i.message).join(' · ') : e.message;
};

/**
 * „Klucz GUS (API REGON)” in Ustawienia firmy, the owner's only (2026-11-03), like the KSeF token: the key field is
 * write-only — after saving only „Klucz zapisany <data>” with „Zmień klucz” and „Usuń”. „Pobierz z GUS” in the contractor
 * form uses it.
 */
export function GusSettings() {
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => api<GusSettingsView>('/api/gus/settings') });
  const [key, setKey] = useState('');
  const [changing, setChanging] = useState(false);
  const done = (view: GusSettingsView) => {
    queryClient.setQueryData(SETTINGS_KEY, view);
    setKey('');
    setChanging(false);
  };
  const save = useMutation({ mutationFn: () => api<GusSettingsView>('/api/gus/settings', { method: 'PUT', body: { key: key.trim() } }), onSuccess: done });
  const remove = useMutation({ mutationFn: () => api<GusSettingsView>('/api/gus/settings/key', { method: 'DELETE' }), onSuccess: done });
  const view = settings.data;
  const busy = save.isPending || remove.isPending;
  const failure = save.error ?? remove.error;

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <section className="settings-section" aria-label="Klucz GUS (API REGON)" data-feature-id="gus.settings">
      <h2>Klucz GUS (API REGON)</h2>
      <p className="hint">Do „Pobierz z GUS” w formularzu kontrahenta: nazwa, adres i REGON z rejestru GUS po NIP. Klucz widzi i zmienia tylko właściciel; po zapisaniu jest zaszyfrowany i nikt go już nie zobaczy.</p>
      {settings.error && (
        <p className="alert" role="alert">
          {errorText(settings.error, 'Nie udało się wczytać ustawień GUS')}
        </p>
      )}
      {view && (
        <form onSubmit={submit}>
          {view.error && (
            <p className="alert" role="alert">
              {view.error}
            </p>
          )}
          {!view.keySet || changing ? (
            <>
              <label className="field">
                <span>Klucz GUS</span>
                <input type="password" value={key} onChange={(e) => setKey(e.target.value)} autoComplete="new-password" spellCheck={false} placeholder="Wklej klucz z e-maila od GUS" />
              </label>
              <div className="actions">
                <button className="btn primary" type="submit" disabled={busy || key.trim() === ''}>
                  {save.isPending ? 'Zapisywanie…' : 'Zapisz klucz'}
                </button>
                {view.keySet && (
                  <button
                    className="link-button"
                    type="button"
                    onClick={() => {
                      setKey('');
                      setChanging(false);
                      save.reset();
                    }}
                  >
                    Anuluj
                  </button>
                )}
              </div>
            </>
          ) : (
            <p>
              <span>{`Klucz zapisany ${view.keySavedAt ? WHEN.format(new Date(view.keySavedAt)) : ''}`.trim()}</span>{' '}
              <button className="link-button" type="button" onClick={() => setChanging(true)}>
                Zmień klucz
              </button>{' '}
              <button className="link-button" type="button" disabled={busy} onClick={() => remove.mutate()}>
                Usuń
              </button>
            </p>
          )}
          {failure && (
            <p className="alert" role="alert">
              {errorText(failure, 'Nie udało się zapisać klucza GUS')}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
